/**
 * Landlord Property Management API Service
 * Handles all API calls for landlord property operations
 */

import { request, unwrapPatternA } from './apiClient';

/**
 * Landlord Property Service
 */
const landlordPropertyService = {
    /**
     * Create a new property listing
     * @param {Object} propertyData - Property information
     * @returns {Promise<{propertyId: number, message: string}>}
     */
    createProperty: async (propertyData) => {
        const data = await request('/landlord/properties/create', {
            method: 'POST',
            body: propertyData
        });
        return unwrapPatternA(data);
    },

    /**
     * Update an existing property
     * @param {number} propertyId - ID of the property to update
     * @param {Object} propertyData - Updated property information
     * @returns {Promise<{propertyId: number, message: string}>}
     */
    updateProperty: async (propertyId, propertyData) => {
        const data = await request(`/landlord/properties/update?propertyId=${propertyId}`, {
            method: 'PUT',
            body: propertyData
        });
        return unwrapPatternA(data);
    },

    /**
     * Get all properties for the logged-in landlord
     * @param {string} [status] - Optional status filter
     * @returns {Promise<{total: number, properties: Array}>}
     */
    getAllProperties: async (status = null) => {
        const path = status
            ? `/landlord/properties/list?status=${status}`
            : '/landlord/properties/list';
        const data = await request(path);
        return unwrapPatternA(data);
    },

    /**
     * Get details of a specific property
     * @param {number} propertyId - ID of the property
     * @returns {Promise<Object>} Property details
     */
    getPropertyDetails: async (propertyId) => {
        const data = await request(`/landlord/properties/details?propertyId=${propertyId}`);
        return unwrapPatternA(data);
    },

    /**
     * Upload an image for a property
     * @param {number} propertyId - ID of the property
     * @param {File} imageFile - Image file to upload
     * @param {Object} metadata - Image metadata (title, description, isFeatured)
     * @returns {Promise<{imageId: number, imageUrl: string, message: string}>}
     */
    uploadImage: async (propertyId, imageFile, metadata = {}) => {
        const formData = new FormData();
        formData.append('image', imageFile);

        if (metadata.title) {
            formData.append('title', metadata.title);
        }

        if (metadata.description) {
            formData.append('description', metadata.description);
        }

        formData.append('isFeatured', metadata.isFeatured ? 'true' : 'false');

        const data = await request(`/landlord/properties/upload-image?propertyId=${propertyId}`, {
            method: 'POST',
            body: formData,
            multipart: true
        });
        return unwrapPatternA(data);
    },

    /**
     * Upload multiple images for a property
     * @param {number} propertyId - ID of the property
     * @param {Array<{file: File, title: string, description: string, isFeatured: boolean}>} images
     * @returns {Promise<Array>} Array of upload results
     */
    uploadImages: async (propertyId, images) => {
        const uploadPromises = images.map((image, index) =>
            landlordPropertyService.uploadImage(propertyId, image.file, {
                title: image.title || '',
                description: image.description || '',
                isFeatured: index === 0 ? true : (image.isFeatured || false)
            })
        );

        return Promise.all(uploadPromises);
    },

    /**
     * Publish a property (make it live)
     * @param {number} propertyId - ID of the property to publish
     * @returns {Promise<{message: string, propertyId: number}>}
     */
    publishProperty: async (propertyId) => {
        const data = await request(`/landlord/properties/publish?propertyId=${propertyId}`, {
            method: 'POST'
        });
        return unwrapPatternA(data);
    },

    /**
     * Delete a property (soft delete)
     * @param {number} propertyId - ID of the property to delete
     * @returns {Promise<{message: string, propertyId: number}>}
     */
    deleteProperty: async (propertyId) => {
        const data = await request(`/landlord/properties/delete?propertyId=${propertyId}`, {
            method: 'DELETE'
        });
        return unwrapPatternA(data);
    }
};

export default landlordPropertyService;
